(() => {
  const initializedRails = new WeakSet();
  const reducedMotionQuery = window.matchMedia?.('(prefers-reduced-motion: reduce)');

  const normalizeWheelDelta = (event) => {
    const delta = Math.abs(event.deltaY) >= Math.abs(event.deltaX) ? event.deltaY : event.deltaX;

    if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return delta * 16;
    if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return delta * window.innerHeight;

    return delta;
  };

  const initRail = (rail) => {
    if (initializedRails.has(rail)) return;

    initializedRails.add(rail);

    let targetScrollLeft = rail.scrollLeft;
    let wheelAnimationFrame = 0;
    const progressBar = rail.closest('.denim-directory__rail')?.querySelector('[data-denim-directory-scrollbar]');
    const progressFill = progressBar?.querySelector('[data-denim-directory-progress]');

    const updateProgress = () => {
      const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
      const isAtEnd = maxScrollLeft - rail.scrollLeft <= 2;
      const progress = maxScrollLeft > 1 && !isAtEnd ? rail.scrollLeft / maxScrollLeft : 1;
      const normalizedProgress = Math.max(0, Math.min(progress, 1));

      if (progressFill) progressFill.style.transform = `scaleX(${normalizedProgress})`;
      progressBar?.setAttribute('aria-valuenow', String(Math.round(normalizedProgress * 100)));
    };

    const handleRailScroll = () => {
      if (!wheelAnimationFrame) targetScrollLeft = rail.scrollLeft;
      updateProgress();
    };

    rail.addEventListener('scroll', handleRailScroll, { passive: true });
    window.addEventListener('resize', updateProgress, { passive: true });

    rail.addEventListener('pointerdown', () => {
      if (wheelAnimationFrame) {
        cancelAnimationFrame(wheelAnimationFrame);
        wheelAnimationFrame = 0;
      }

      targetScrollLeft = rail.scrollLeft;
    }, { passive: true });

    const animateWheelScroll = () => {
      const distance = targetScrollLeft - rail.scrollLeft;

      if (Math.abs(distance) < 0.5) {
        rail.scrollLeft = targetScrollLeft;
        wheelAnimationFrame = 0;
        updateProgress();
        return;
      }

      rail.scrollLeft += distance * 0.22;
      wheelAnimationFrame = requestAnimationFrame(animateWheelScroll);
    };

    const moveToScrollPosition = (nextScrollLeft) => {
      const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);

      targetScrollLeft = Math.min(maxScrollLeft, Math.max(0, nextScrollLeft));

      if (reducedMotionQuery?.matches) {
        rail.scrollLeft = targetScrollLeft;
        updateProgress();
        return;
      }

      if (!wheelAnimationFrame) {
        wheelAnimationFrame = requestAnimationFrame(animateWheelScroll);
      }
    };

    rail.addEventListener('wheel', (event) => {
      const maxScrollLeft = rail.scrollWidth - rail.clientWidth;
      const delta = normalizeWheelDelta(event);

      if (maxScrollLeft <= 1 || delta === 0) return;

      const scrollBase = wheelAnimationFrame ? targetScrollLeft : rail.scrollLeft;
      const nextScrollLeft = Math.min(maxScrollLeft, Math.max(0, scrollBase + delta));
      const movingPastStart = delta < 0 && scrollBase <= 0.5;
      const movingPastEnd = delta > 0 && scrollBase >= maxScrollLeft - 0.5;

      if (movingPastStart || movingPastEnd || Math.abs(nextScrollLeft - scrollBase) < 0.5) return;

      event.preventDefault();
      moveToScrollPosition(nextScrollLeft);
    }, { passive: false });

    progressBar?.addEventListener('click', (event) => {
      const bounds = progressBar.getBoundingClientRect();

      if (bounds.width <= 0) return;

      const clickProgress = (event.clientX - bounds.left) / bounds.width;
      moveToScrollPosition(clickProgress * (rail.scrollWidth - rail.clientWidth));
    });

    progressBar?.addEventListener('keydown', (event) => {
      const maxScrollLeft = Math.max(0, rail.scrollWidth - rail.clientWidth);
      const step = Math.max(160, rail.clientWidth * 0.75);
      let nextScrollLeft = null;

      if (event.key === 'ArrowLeft' || event.key === 'PageUp') nextScrollLeft = targetScrollLeft - step;
      if (event.key === 'ArrowRight' || event.key === 'PageDown') nextScrollLeft = targetScrollLeft + step;
      if (event.key === 'Home') nextScrollLeft = 0;
      if (event.key === 'End') nextScrollLeft = maxScrollLeft;

      if (nextScrollLeft === null) return;

      event.preventDefault();
      moveToScrollPosition(nextScrollLeft);
    });

    requestAnimationFrame(() => {
      rail.scrollLeft = 0;
      targetScrollLeft = 0;
      updateProgress();
    });
  };

  const init = (scope = document) => {
    scope.querySelectorAll?.('.denim-directory__fits').forEach(initRail);

    if (scope.matches?.('.denim-directory__fits')) initRail(scope);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
